import api from '../services/api';
import { $Qll, $Q } from '../utils/querySelector';
import { stringToHTML } from '../utils/toHtml';
import { RefreshPage } from './preorderVariant';
import { currentLang } from '../utils/getLanguage';
import { redirect } from '../utils/redirect';
import { variantOnChange } from '../components/variantsProduct';
import { Discounts } from './discountCode';

/**
 * Bind the change handler for the sticky nav's variant select, so picking
 * an option there updates its price, stock/preorder state and button.
 * Exported so it can be re-attached after the select is replaced in the DOM
 * (e.g. by kit-options.js after swapping in a new variant's markup).
 * @param {HTMLSelectElement} selectVariants
 * @author Michael Ballen <michael.ballen@gradiweb.com>
 */
export const bindStickySelectChange = (selectVariants) => {
  if (!selectVariants) return;
  // Guard against duplicate bindings: `new Product()` (called on every
  // radio/select change to reach getVariantInfo()) re-runs this from its
  // constructor each time, and the select itself is never replaced outside
  // of a kit-variant swap - without this flag every change adds one more
  // listener, so each subsequent change fires N duplicate getVariantInfo()/
  // discount lookups. A freshly swapped-in select (kit-options.js) has no
  // flag yet, so it still gets bound correctly.
  if (selectVariants.dataset.changeBound) return;
  selectVariants.dataset.changeBound = 'true';

  // A native <select> fires 'change' on every option a keyboard/wheel
  // interaction passes through, not just the one the user settles on.
  // Debounce the expensive main-page sync so only the final selection
  // triggers a getVariantInfo()/discount lookup instead of one per tick.
  let syncMainFormTimeout;

  selectVariants.addEventListener('change', function (e) {
    const option = e.target.selectedOptions[0]
    const button = document.querySelector('.btn-sticky-cart-js')
    const form = document.querySelector('#add-to-cart-form-sticky')

    // Keep the main product page's variant picker (radios, price, stock,
    // button) in sync with the sticky select. This mirrors the same
    // getVariantInfo() call used when a radio in the main picker changes.
    // Only do this for a real user interaction (e.isTrusted): the reverse
    // sync in selectVariant() (variantsProduct.js) sets this select's value
    // and dispatches a synthetic 'change' to refresh the sticky UI itself;
    // without this guard that synthetic event would re-enter this handler
    // and re-trigger getVariantInfo(), duplicating the request it originated from.
    const variantsContainer = document.querySelector('.product__description--variantform .variants')
    const handle = variantsContainer?.querySelector('[name="handle"]')?.value
    if (e.isTrusted && variantsContainer && handle) {
      const mainFormIdInput = document.querySelector('#page-product-form [name="id"]')
      if (mainFormIdInput) mainFormIdInput.value = option.value

      clearTimeout(syncMainFormTimeout)
      syncMainFormTimeout = setTimeout(() => {
        const product = new Product()
        product.getVariantInfo(option.value, handle).then(() => {
          // Scope to #page-product-form so the discount lookup reads the
          // variant id we just updated, instead of falling back to the first
          // [name="id"] element anywhere in the document (Discounts' constructor
          // already runs the single lookup and syncs the sticky price display).
          new Discounts('#page-product-form')
        })
      }, 300)
    }

    // Update prices
    document.querySelector('.atc-component__price').textContent = option.dataset.price
    document.querySelector('.atc-component__compare-at-price').textContent = option.dataset.compare || ''
    if (option.dataset.compare) document.querySelector('.atc-component__compare-at-price').removeAttribute('hidden')
    else document.querySelector('.atc-component__compare-at-price').setAttribute('hidden', '')

    // Determine stock status
    const available = option.dataset.available === 'true'
    const inventory = parseInt(option.dataset.inventory)
    const policy = option.dataset.policy
    const hasPreorderTag = option.dataset.hasPreorderTag !== '0'

    const isPreorder = available && inventory <= 0 && policy === 'continue' && hasPreorderTag
    const isOutOfStock = !available && !isPreorder

    // Update/remove preorder property
    let preorderInput = form.querySelector('.preorder-property')
    if (isPreorder) {
      if (!preorderInput) {
        preorderInput = document.createElement('input')
        preorderInput.type = 'hidden'
        preorderInput.name = 'properties[preorder]'
        preorderInput.value = option.dataset.preorder
        preorderInput.className = 'preorder-property'
        form.appendChild(preorderInput)
      }
    } else {
      if (preorderInput) {
        preorderInput.remove()
      }
    }

    // Update button. Always replace the node (rather than mutating it in
    // place) so any click listener bound while out-of-stock (registered by
    // backInStockRegisterEvents) is discarded instead of lingering on a
    // button that has since gone back to "add to cart" behavior.
    const newButton = document.createElement('button')
    newButton.className = 'atc-component__button btn-sticky-cart-js'

    if (!isOutOfStock) {
      newButton.type = 'submit'
      newButton.textContent = isPreorder ? form.dataset.preorderText : form.dataset.addToCartText
      button.replaceWith(newButton)
    } else {
      newButton.type = 'button'
      newButton.classList.add('klaviyo-bis-trigger')
      const productTitle = document.querySelector('#atc-product-title')?.textContent?.trim()
      const productVariants = document.querySelector('#variants')?.value
      if (productTitle) newButton.setAttribute('data-product-title', productTitle)
      if (productVariants) newButton.setAttribute('data-product-variants', productVariants)
      newButton.textContent = form.dataset.backInStockText
      button.replaceWith(newButton)
      window.backInStockRegisterEvents()
    }
  })
}

/**
 * Product functionalities
 * @author Michael Ballen <michael.ballen@gradiweb.com>
 */
class Product {
  constructor(query, linksquery, engraving) {
    this.productDescription = $Q(query);
    this.linksContainer = $Q(linksquery);
    this.engravingBtn = document.querySelectorAll(engraving);
    this.stickyNav = $Q('.nav-sticky');

    if (this.productDescription) {
      this._init();
    }

    // `new Product()` is instantiated on every variant change just to reach
    // getVariantInfo() (see bindStickySelectChange() and renderVariantInfo()
    // in variantsProduct.js) - guard the one-time nav bindings below so they
    // don't pile up another click listener per anchor on each of those calls.
    if (this.stickyNav && !this.stickyNav.dataset.navBound) {
      this.stickyNav.dataset.navBound = 'true';
      const selectVariants = $Q('select[name="id"]', this.stickyNav);

      document.querySelectorAll('.nav-sticky a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', this._smoothScrollWithOffset);
      });

      bindStickySelectChange(selectVariants);
    }

    if ($Q('#MainContent')) {
      this.addCurrentLang();
    }

    if ($Qll('.js-prod-card-btn-klaviyo')) {
      /* this.redirectProductCardToProduct(); */
    }
    if (this.engravingBtn) {
      this._tooggleEngraving();
    }
  }

  /**
     * Init prouct characters for description
     * @author Michael Ballen <michael.ballen@gradiweb.com>
     */
  _init() {
    this._countCharacters();
    this._validateLinks();
    this._validateSwiper();
  }

  /**
   * Validate if product description have a long description
   * @author Michael Ballen <michael.ballen@gradiweb.com>
   * @see _readMore()
   */
  _countCharacters() {
    const fullDescription = this.productDescription.innerHTML;
    if (fullDescription.includes('<p>...</p>')) {
      this._readMore(fullDescription);
    }
  }

  _validateLinks() {
    const dowloadLink = $Q(
      '#product__description-dowload-link',
      this.linksContainer
    );
    const specLink = $Q('#product__description-spec-link', this.linksContainer);

    if (dowloadLink != null) {
      const dowloadAccordion = $Q('#dowloads');

      if (dowloadAccordion === null) {
        dowloadLink.classList.add('hidden');
      }
    }

    if (specLink != null) {
      const specAccordion = $Q('#spec');

      if (specAccordion === null) {
        specLink.classList.add('hidden');
      }
    }
  }

  /**
   * Enable link for extend description
   * @param {String} string
   * @author Michael Ballen <michael.ballen@gradiweb.com>
   * @see _showHiddeText()
   */
  _readMore(string) {
    const descriptions = string.split('<p>...</p>');
    const descriptionPreview = descriptions[0];
    const description2 = descriptions[1];
    const readMoreText = $Q('#read-more-text').value;
    const readlessText = $Q('#read-less-text').value;

    this.productDescription.innerHTML = `
      ${descriptionPreview}
      <span id="more" class="more">${description2}</span>
      <span id="dots" class="dots">${readMoreText}</span>
    `;

    const readMoreButton = $Q('#dots');
    const secondDescription = $Q('#more');

    // show other botton when this is clicked
    readMoreButton.addEventListener(
      'click',
      this._showHiddeText.bind(
        this,
        readMoreButton,
        secondDescription,
        readMoreText,
        readlessText
      )
    );
  }

  /**
   * Extend all description and change text for the link
   * @param {Node} button
   * @param {Node} text
   * @param {String} readMoreText
   * @param {String} readlessText
   * @author Michael Ballen <michael.ballen@gradiweb.com>
   */
  _showHiddeText(button, text, readMoreText, readlessText) {
    text.classList.toggle('more-show');

    if (text.classList.contains('more-show')) {
      button.innerText = readlessText;
    } else {
      button.innerText = readMoreText;
    }
  }

  /**
   * Render in DOM if selected variant have stock
   * @param {String} variantInfo
   * @author Michael Ballen <michael.ballen@gradiweb.com>
   */
  _changeStockMessage(variantInfo) {
    const elements = {
      validateVariant: $Q('#validate-stock', stringToHTML(variantInfo)),
      validateContainer: $Q('#validate-stock'),
      stockElement: $Q('.product__description--price-stock'),
      stockText: $Q('#stock-text')
    };

    if (!elements.validateVariant || !elements.validateContainer || !elements.stockElement) {
      return;
    }

    const stockData = {
      message: elements.validateVariant.getAttribute('data-stock-text'),
      isInStock: elements.validateVariant.getAttribute('data-stock') === 'true'
    };

    elements.stockElement.classList.toggle('in_stock', stockData.isInStock);
    elements.stockElement.classList.toggle('out_stock', !stockData.isInStock);

    elements.stockText.innerText = stockData.message;

  }

  /**
   * Render in DOM the selected variant price
   * @param {String} variantInfo
   * @author Michael Ballen <michael.ballen@gradiweb.com>
   */
  _changeVariantPrice(variantInfo) {
    const variantHtml = stringToHTML(variantInfo)

    const priceContainer = $Q(
      '.product__description--price-prices',
      variantHtml
    );
    const priceContainerDom = $Q('.product__description--price-prices');
    const stickyPriceContainer = $Q(
      '.stickyaddcart__product-price',
      variantHtml
    );
    const stickyPriceContainerDom = $Q('.stickyaddcart__product-price');

    const newCustomDiscountEl = $Q('.custom-discount', variantHtml)
    const actualDiscount = $Q('.custom-discount')
    if (newCustomDiscountEl && actualDiscount) {
      const dataValue = newCustomDiscountEl.dataset.price
      actualDiscount.dataset.price = dataValue
    }

    priceContainerDom.innerHTML = priceContainer.innerHTML;
    if (stickyPriceContainerDom) {
      stickyPriceContainerDom.innerHTML = stickyPriceContainer.innerHTML;
    }
  }

  _changeButton(variantInfo) {
    const buttonContainer = $Q(
      '.product__description--variantform-button',
      stringToHTML(variantInfo)
    );
    const buttonContainerDom = $Q('.product__description--variantform-button');
    const stickyButtonContainer = $Q(
      '.stickyaddcart__buttons',
      stringToHTML(variantInfo)
    );
    const stickyButtonContainerDom = $Q('.stickyaddcart__buttons');

    buttonContainerDom.innerHTML = buttonContainer.innerHTML;
    if (stickyButtonContainerDom) {
      stickyButtonContainerDom.innerHTML = stickyButtonContainer.innerHTML;
    }

    document.querySelectorAll('span.engraving__event').forEach((btn) => {
      btn.addEventListener('click', () => {
        const modalEngraving = document.querySelector('#modal-engraving');
        modalEngraving.classList.contains('active')
          ? modalEngraving.classList.remove('active')
          : modalEngraving.classList.add('active');
      });
    });
  }

  _changeVariantInfo(variantInfo) {
    const variantContainer = $Q(
      '.product__description--variantform .variants',
      stringToHTML(variantInfo)
    );
    const variantContainerDom = $Q('.product__description--variantform .variants');

    variantContainerDom.innerHTML = variantContainer.innerHTML;

    variantOnChange('.variants');
  }

  /**
   * Render in DOM the secondary text for products in preorder state
   * @param {String} variantInfo
   * @author Luis Cortés <luis.cortes@gradiweb.com>
   */
  _changeSecondaryText(variantInfo) {
    const secondaryText = $Q(
      '.product__description--variantform-secondary-text',
      stringToHTML(variantInfo)
    );
    const secondaryTextDom = $Q('.product__description--variantform-secondary-text');

    const available = secondaryText.dataset.available;
    const inventory = secondaryText.dataset.inventory;
    const policy = secondaryText.dataset.policy;
    const hasPreorderTag = secondaryText.dataset.hasPreorderTag;
    const preorder = secondaryText.dataset.preorder;

    const isPreorder = available && inventory <= 0 && policy === 'continue' && hasPreorderTag

    if (isPreorder) {
      secondaryTextDom.style.display = '';
      secondaryTextDom.innerText = preorder;
    } else {
      secondaryTextDom.style.display = 'none';
      secondaryTextDom.innerText = '';
    }
  }

  /**
   * Render in DOM the backorder message
   * @param {String} variantInfo
   * @author Luis Cortés <luis.cortes@gradiweb.com>
   */
  _changeBackorderMessage(variantInfo) {
    const backorderContainer = $Q(
      '.product__description--backorder',
      stringToHTML(variantInfo)
    );
    const backorderContainerDom = $Q('.product__description--backorder');

    if (backorderContainer && backorderContainerDom) {
      backorderContainerDom.innerHTML = backorderContainer.innerHTML;
    }
  }

  /**
   * Render in DOM the selected variant image in slider
   * @param {String} variantInfo
   * @author Michael Ballen <michael.ballen@gradiweb.com>
   */
  _changeImageSlider(variantInfo) {
    const sliderContainer = $Q(
      '.product__slider .swiper-wrapper',
      stringToHTML(variantInfo)
    );
    const sliderContainerDom = $Q('.product__slider .swiper-wrapper');
    const stickyImage = $Q(
      '.stickyaddcart__product-info',
      stringToHTML(variantInfo)
    );
    const stickyImageDom = $Q('.stickyaddcart__product-info');

    sliderContainerDom.innerHTML = sliderContainer.innerHTML;
    if (stickyImageDom) {
      stickyImageDom.innerHTML = stickyImage.innerHTML;
    }
  }

  /**
   * Get the HTML section from selected variant
   * @param {String} variantId
   * @param {String} handle
   * @author Michael Ballen <michael.ballen@gradiweb.com>
   * @see _changeImageSlider()
   * @see _changeStockMessage()
   * @see _changeVariantPrice()
   */
  async getVariantInfo(variantId, handle) {
    const base = `/products/${handle}?variant=${variantId}`;
    let localizedBase = base;
    const response = await api.shopifySectionByUrl(base, 'main-product-new');

    let localizedResponse = response;
    if (currentLang && currentLang !== 'en') {
      localizedBase = `/${currentLang}/products/${handle}?variant=${variantId}`;
      localizedResponse = await api.shopifySectionByUrl(localizedBase, 'main-product-new');
    }

    this.enableLoader();

    this._changeStockMessage(localizedResponse);
    this._changeVariantPrice(response);
    //this._changeImageSlider(response);
    this._changeButton(localizedResponse);
    this._changeVariantInfo(response);
    this._changeSecondaryText(localizedResponse);
    this._changeBackorderMessage(localizedResponse);
    this.disabledLoader();
    this.changeSelectValue();
    RefreshPage();
  }

  /**
   * Add attribute to the tag main with the current laguange
   * @author Daniel Santos <daniel.santos@gradiweb.com>
   */
  addCurrentLang() {
    const main = $Q('#MainContent');
    main.setAttribute('data-isLang', currentLang);
    main.setAttribute('data-isLang', currentLang);
  }

  /**
   * Redirect product card to the product page when button is "Notify me".
   * @author Daniel Santos <daniel.santos@gradiweb.com>
   */
  redirectProductCardToProduct() {
    $Qll('.js-prod-card-btn-klaviyo').forEach((btn) => {
      btn.addEventListener('click', () => redirect(`${btn.dataset.href}`));
    });
  }

  enableLoader() {
    const loaders = $Qll('.loading-overlay');
    const stickyImage = $Q('.stickyaddcart__product-info--img');
    const stickyVariants = $Q(
      '.stickyaddcart__variant-selector .add-cart-form'
    );

    loaders.forEach((loader) => {
      loader.classList.remove('hidden');
    });

    if (stickyImage) {
      stickyImage.classList.add('hidden');
    }
    if (stickyVariants) {
      stickyVariants.classList.add('hidden');
    }
  }

  disabledLoader() {
    const loaders = $Qll('.loading-overlay');
    const stickyVariants = $Q(
      '.stickyaddcart__variant-selector .add-cart-form'
    );

    loaders.forEach((loader) => {
      loader.classList.add('hidden');
    });

    if (stickyVariants) {
      stickyVariants.classList.remove('hidden');
    }
  }

  changeSelectValue() {
    const select = $Q('.variants--select-tag[name="Colour"]');
    const value =
      $Q(
        '.variant_radio-button--design span',
        $Q('.stickyaddcart__container')
      ) &&
      $Q('.variant_radio-button--design span', $Q('.stickyaddcart__container'))
        .dataset.selectedColor;

    if (value) {
      const colorVariant = $Q(
        `.option_${value.toLowerCase()}`,
        $Q('.product__description--variantform')
      );

      select.value = value;
      colorVariant.click();
    }
  }

  /**
   * Validate if the points of the slider are greater than one
   * @author Sebastián García <sebastian.garcia@gradiweb.com>
   */
  _validateSwiper() {
    const sliderPrincipal = $Qll('.main-product__slider-principal');
    const productSlider = $Qll('.product-slider');

    if (!sliderPrincipal) return;
    const swiperPagination = $Qll('.swiper-pagination', sliderPrincipal[0])[0];
    if (swiperPagination?.children?.length <= 1) {
      swiperPagination.classList.add('hidden');
      $Qll('.swiper-button-next', productSlider[0])[0].classList.add('hidden');
      $Qll('.swiper-button-prev', productSlider[0])[0].classList.add('hidden');
    }
  }

  /**
   * Open engraving
   * @author Miguel Camacho <miguel.camacho@gradiweb.com>
   */
  _tooggleEngraving() {
    const modalEngraving = document.querySelector('#modal-engraving');

    this.engravingBtn.forEach((btn) => {
      btn.addEventListener('click', () => {
        modalEngraving.classList.contains('active')
          ? modalEngraving.classList.remove('active')
          : modalEngraving.classList.add('active');
      });
    });
  }

  /**
   * Smooth scroll with offset
   * @author Miguel Camacho <camilosaenz115@gmail.com>
   */

  _smoothScrollWithOffset(event) {
    event.preventDefault();

    const targetId = event.currentTarget.getAttribute('href');
    const targetElement = document.querySelector(targetId);

    document.querySelectorAll('.nav-sticky li.active').forEach(el => {
      el.classList.remove('active');
    });

    event.currentTarget.closest('li').classList.add('active');

    const navHeight = document.querySelector('.nav-sticky').offsetHeight;

    const elementPosition = targetElement.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - navHeight;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }

}

export { Product };
